import { useRef, useState, useLayoutEffect, useCallback } from "react";

/**
 * Centered Search Width Hook
 * ---------------------------------
 * Goal: Keep the desktop search bar centered between the left & right toolbar clusters
 * without overlapping them, while respecting a min and max width.
 *
 * Visual layout (simplified):
 * [ Left cluster ]  |   < centered search >   |  [ Right cluster ]
 *
 * How width is calculated (per recompute):
 * 1. Find the horizontal center of the toolbar.
 * 2. Measure free space from that center to the right edge of the left cluster.
 * 3. Measure free space from the center to the left edge of the right cluster.
 * 4. Take the smaller of those two (that's the limiting side).
 * 5. Double it to get the total width available for a centered element.
 * 6. Clamp that width between MIN_WIDTH and MAX_WIDTH.
 *
 * When does it recalculate?
 * - On first mount.
 * - Whenever either cluster (or the toolbar) changes size (ResizeObserver).
 * - On window resize.
 * - When a custom 'recompute-centered-search-width' event is dispatched (e.g. on focus).
 *
 * The chosen approach deliberately avoids timers, mutation observers, and complex retries.
 * If something changes that alters layout width, one of the above triggers will handle it.
 */

// Design constraints: adjust these if design requirements change.
// We enforce a lower bound so the input is never too cramped, and an upper bound
// so it doesn't grow awkwardly wide on very large screens.
const MIN_WIDTH = 400;
const MAX_WIDTH = 800;

export const useCenteredSearchWidth = () => {
  // Refs that Layout attaches to left & right toolbar clusters
  const leftClusterRef = useRef<HTMLDivElement | null>(null);
  const rightClusterRef = useRef<HTMLDivElement | null>(null);

  // Start with a reasonable default; it will be replaced on the first measurement.
  const [widthPx, setWidthPx] = useState<number>(MIN_WIDTH);

  // Core width calculation. Safe to call any time.
  // If refs are not yet mounted, it just returns early.
  const computeWidth = useCallback(() => {
    const toolbar = document.getElementById("toolbar");
    const left = leftClusterRef.current;
    const right = rightClusterRef.current;
    if (!toolbar || !left || !right) return;

    const toolbarRect = toolbar.getBoundingClientRect();
    const leftRect = left.getBoundingClientRect();
    const rightRect = right.getBoundingClientRect();
    const centerX = toolbarRect.left + toolbarRect.width / 2;

    // Distance from the toolbar center line to the inner edge of each cluster.
    const halfSpaceLeft = centerX - leftRect.right;
    const halfSpaceRight = rightRect.left - centerX;

    // Pick whichever side is tighter (smallest free half-width).
    const limitingHalf = Math.min(halfSpaceLeft, halfSpaceRight);
    const totalAvailable = Math.max(0, limitingHalf * 2); // full space between clusters
    if (totalAvailable <= 0) {
      return; // Not enough space yet (e.g. clusters overlapping center)
    }

    const bounded = Math.max(MIN_WIDTH, totalAvailable);
    const clamped = Math.min(MAX_WIDTH, bounded);
    setWidthPx((prev) => (prev === clamped ? prev : clamped));
  }, []);

  // Initial measurement + observers.
  // ResizeObserver: fires if the clusters or toolbar grow/shrink (icons appear, text changes, etc.).
  useLayoutEffect(() => {
    computeWidth(); // initial measurement (refs present after first paint)

    const observers: ResizeObserver[] = [];
    const toolbar = document.getElementById("toolbar");
    [leftClusterRef.current, rightClusterRef.current, toolbar].forEach((el) => {
      if (!el) return;
      const ro = new ResizeObserver(() => computeWidth());
      ro.observe(el);
      observers.push(ro);
    });

    window.addEventListener("resize", computeWidth);
    // Allow manual external triggers (e.g. the search input focus) via custom event.
    window.addEventListener(
      "recompute-centered-search-width",
      computeWidth as EventListener
    );

    return () => {
      observers.forEach((o) => o.disconnect());
      window.removeEventListener("resize", computeWidth);
      window.removeEventListener(
        "recompute-centered-search-width",
        computeWidth as EventListener
      );
    };
  }, [computeWidth]);

  // Expose the width to CSS (Tailwind uses max-w-[var(--search-max-w)] in Layout).
  useLayoutEffect(() => {
    document.documentElement.style.setProperty(
      "--search-max-w",
      widthPx + "px"
    );
  }, [widthPx]);

  // Consumers get refs, the current width (for debugging / data attributes),
  // and a recompute function (rarely needed, but available for manual triggers/tests).
  return { leftClusterRef, rightClusterRef, widthPx, recompute: computeWidth };
};
